import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LoginForm from "./LoginForm";
import "./AlartSetFormFixedPrice.css";
import config from "../config"; // adjust the path as needed

const AlartSetFormFixedPrice = ({ functionId, functionName }) => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showLoginModal, setShowLoginModal] = useState(false);

  // ✅ Check login
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) setShowLoginModal(true);
  }, []);

  // ✅ Fetch data
  useEffect(() => {
    if (showLoginModal) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");

        const res = await fetch(
          `${config.API_BASE_URL}alert/user-parameter/?function_id=2`,
          {
            headers: {
              Authorization: `token ${token}`,
            },
          }
        );

        if (!res.ok) {
          const err = await res.json();
          toast.error(err.error || "Failed to load data");
          setLoading(false);
          return;
        }
        const apiData = await res.json();
        console.log(apiData)

        const mapped = apiData.map((item, i) => {
          let lower = "";
          let higher = "";

          try {
            const parsed = JSON.parse(item.user_parameter?.set_price || "{}");
            lower = parsed.lower || "";
            higher = parsed.higher || "";
          } catch {
            lower = "";
            higher = "";
          }

          // ✅ Remove comma (thousand separator) safely
          let presentPrice = item.close;
          if (typeof presentPrice === "string") {
            presentPrice = presentPrice.replace(/[^0-9.]/g, "");
          }

          return {
            sl: i + 1,
            trading_code: item.trading_code,
            present_price: presentPrice,
            lower,
            higher,
            is_active: item.is_active || false,
            is_repeat: item.is_repeat || false,
          };
        });

        setData(mapped);
        setFilteredData(mapped);
      } catch (err) {
        console.error("Error fetching:", err);
        toast.error("Network error while loading data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [functionId, showLoginModal]);

  // ✅ Search filter
  useEffect(() => {
    const lower = searchTerm.toLowerCase();
    setFilteredData(
      data.filter((row) => row.trading_code.toLowerCase().includes(lower))
    );
  }, [searchTerm, data]);

  // ✅ Handle input change (allow positive decimals)
  const handleChange = (index, field, value) => {
    if (field === "lower" || field === "higher") {
      if (value < 0) {
        toast.warning("⚠️ Price cannot be negative");
        return;
      }

      // Allow only valid positive decimal numbers
      if (!/^\d*\.?\d*$/.test(value)) return;
    }

    const updated = [...filteredData];
    updated[index][field] = value;

    // Disable alert if both lower & higher are empty
    if (!updated[index].lower && !updated[index].higher) {
      updated[index].is_active = false;
      updated[index].is_repeat = false;
    }

    setFilteredData(updated);
    const globalUpdated = data.map((row) =>
      row.trading_code === updated[index].trading_code ? updated[index] : row
    );
    setData(globalUpdated);
  };

  // ✅ Handle checkbox toggle with validation
  const handleCheckboxChange = (index, field, checked) => {
    const row = filteredData[index];
    if (!row.lower && !row.higher && checked) {
      toast.info("⚠️ Please input lower or higher price to activate alert.");
      return;
    }

    const updated = [...filteredData];
    updated[index][field] = checked;

    // ✅ Do NOT auto-select 'Once' when 'Repeat' is selected
    if (field === "is_repeat" && checked) {
      // only toggle repeat, do not change is_active
    }

    // Disable both if both prices are empty
    if (!updated[index].lower && !updated[index].higher) {
      updated[index].is_active = false;
      updated[index].is_repeat = false;
    }

    setFilteredData(updated);
    const globalUpdated = data.map((row) =>
      row.trading_code === updated[index].trading_code ? updated[index] : row
    );
    setData(globalUpdated);
  };

  // ✅ Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");

    const entries = data
      .filter(
        (row) =>
          (row.is_active || row.is_repeat || row.lower || row.higher) &&
          (row.lower || row.higher)
      )
      .map((row) => ({
        trading_code: row.trading_code,
        is_active: row.is_active,
        is_repeat: row.is_repeat,
        user_parameter: {
          set_price: JSON.stringify({ lower: row.lower, higher: row.higher }),
        },
      }));

    if (entries.length === 0) {
      toast.info("No alerts selected or changed.");
      return;
    }

    setSaving(true);

    try {
      const res = await fetch(`${config.API_BASE_URL}alert/user-parameter/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `token ${token}`,
        },
        body: JSON.stringify({ function_id: 2, entries }),
      });

      const result = await res.json();
      if (res.ok) {
        toast.success(
          <div style={{ textAlign: "center" }}>
            ✅ {result.message || "Alerts saved successfully!"}
            <br />
            <button
              style={{
                marginTop: "8px",
                background: "#007bff",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                padding: "6px 12px",
                cursor: "pointer",
                fontWeight: "bold",
                transition: "0.2s",
              }}
              onClick={() => window.history.back()}
              onMouseEnter={(e) => (e.target.style.background = "#0056b3")}
              onMouseLeave={(e) => (e.target.style.background = "#007bff")}
            >
              🔙 Go Back
            </button>
          </div>,
          { autoClose: false }
        );
      } else {
        toast.error(`❌ ${result.error || "Failed to save alerts"}`);
      }
    } catch (err) {
      toast.error("⚠️ Network error.");
    } finally {
      setSaving(false);
    }
  };

  // ✅ Login modal actions
  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    window.location.reload();
  };

  const handleModalClose = () => {
    window.history.back();
  };

  if (loading && !showLoginModal)
    return <p className="loading-text">Loading...</p>;

  return (
    <>
      {/* Login Modal */}
      {showLoginModal && (
        <div className="modal-overlay" onClick={handleModalClose}>
          <motion.div
            className="modal-content"
            initial={{ opacity: 1, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={(e) => e.stopPropagation()}
          >
            <LoginForm onLoginSuccess={handleLoginSuccess} />
            <button className="modal-close-btn" onClick={handleModalClose}>
              ✖
            </button>
          </motion.div>
        </div>
      )}

      {/* Alert Form */}
      {!showLoginModal && (
        <motion.div
          className="alart-container"
          initial={{ opacity: 1, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="alart-title">🔔 Set Alerts for {functionName}</h2>

          <div className="search-bar-container">
            <input
              type="text"
              className="search-bar"
              placeholder="Search stock by trading code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <form onSubmit={handleSubmit}>
            {/* ✅ Scrollable table container */}
            <div
              className="table-wrapper"
              style={{
                maxHeight: "65vh",
                overflowY: "auto",
                marginBottom: "10px",
              }}
            >
              <table className="alart-table">
                <thead>
                  <tr>
                    <th>Sl.</th>
                    <th>Stock</th>
                    <th>Present Price</th>
                    <th>Lower</th>
                    <th>Higher</th>
                    <th>Once</th>
                    <th>Repeat</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((row, index) => (
                    <motion.tr
                      key={row.trading_code}
                      className="alart-row"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.02 }}
                    >
                      <td>{row.sl}</td>
                      <td className="stock-name">{row.trading_code}</td>
                      <td className="present-price">{row.present_price}</td>

                      {/* Lower price */}
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="any"
                          value={row.lower}
                          onChange={(e) =>
                            handleChange(index, "lower", e.target.value)
                          }
                          placeholder="Lower"
                          className="set-price-input"
                        />
                      </td>

                      {/* Higher price */}
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="any"
                          value={row.higher}
                          onChange={(e) =>
                            handleChange(index, "higher", e.target.value)
                          }
                          placeholder="Higher"
                          className="set-price-input"
                        />
                      </td>

                      {/* Once */}
                      <td>
                        <label className="switch">
                          <input
                            type="checkbox"
                            checked={row.is_active}
                            onChange={(e) =>
                              handleCheckboxChange(
                                index,
                                "is_active",
                                e.target.checked
                              )
                            }
                          />
                          <span className="slider once"></span>
                        </label>
                      </td>

                      {/* Repeat */}
                      <td>
                        <label className="switch">
                          <input
                            type="checkbox"
                            checked={row.is_repeat}
                            onChange={(e) =>
                              handleCheckboxChange(
                                index,
                                "is_repeat",
                                e.target.checked
                              )
                            }
                          />
                          <span className="slider repeat"></span>
                        </label>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ✅ Always visible Save button */}
            <div
              className="submit-section"
              style={{
                position: "sticky",
                bottom: 0,
                background: "#fff",
                padding: "10px 0",
                textAlign: "center",
              }}
            >
              <button type="submit" className="submit-btn" disabled={saving}>
                {saving ? "💾 Saving Alerts..." : "🚀 Save Alerts"}
              </button>
            </div>
          </form>

          <ToastContainer position="top-center" autoClose={3000} />
        </motion.div>
      )}
    </>
  );
};

export default AlartSetFormFixedPrice;
